import { Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { persistState } from '@ngneat/elf-persist-state';
import * as localForage from 'localforage';

// Configure LocalForage to write this store to IndexedDB
localForage.config({
  driver: localForage.INDEXEDDB,
  name: 'pencilapp',
  version: 1.0,
  storeName: 'uiprops',
});

// Interface for stroke settings (pen and highlighter)
interface StrokeSettings {
  strokeWidth?: number;
  strokeColor?: string;
}

// Tool types enum
export enum DrawToolType {
  PEN = 'pen',
  HIGHLIGHTER = 'highlighter',
}

export interface IUIProps {
  spacesVisitedUniqueDays: number;
  spacesVisitedLastDate: Date | null;
  spaceFontFamily: string;
  spaceFontSize?: number;
  spaceFontColor?: string;
  // Draw tool settings
  penStrokeWidth?: number;
  penStrokeColor?: string;
  // Highlight tool settings
  highlighterStrokeWidth?: number;
  highlighterStrokeColor?: string;
  // Global branding theme preference
  globalThemePreference?: string;
}

@Injectable({ providedIn: 'root' })
export class UIPropsRepository {
  private readonly store = createStore(
    { name: 'uiprops' },
    withProps<IUIProps>({
      spacesVisitedUniqueDays: 0,
      spacesVisitedLastDate: null,
      spaceFontFamily: '',
      spaceFontSize: undefined,
      spaceFontColor: undefined,
      // Default draw tool settings - will be overridden if saved
      penStrokeWidth: undefined,
      penStrokeColor: undefined,
      // Default highlight tool settings - will be overridden if saved
      highlighterStrokeWidth: undefined,
      highlighterStrokeColor: undefined,
      // Global branding theme preference
      globalThemePreference: undefined,
    }),
  );

  private readonly persistState = persistState(this.store, {
    key: 'uiprops',
    storage: localForage as any,
  });

  constructor() {
    this.persistState.initialized$.subscribe((result) => {
      console.log(`UIProps store initialized = ${result}`);
    });
  }

  get UIProps$() {
    return this.store.pipe(select((state) => state));
  }

  get uiProps(): IUIProps {
    return this.store.getValue();
  }

  public initialized$ = this.persistState.initialized$;

  updateUIProps(updatedProps: Partial<IUIProps>) {
    this.store.update((state) => ({
      ...state,
      ...updatedProps,
    }));
  }

  // Get stroke settings for a specific tool type
  getDrawToolSettings(toolType: DrawToolType): StrokeSettings {
    const settings = this.store.getValue();

    if (toolType === DrawToolType.PEN) {
      return {
        strokeWidth: settings.penStrokeWidth,
        strokeColor: settings.penStrokeColor,
      };
    }
    return {
      strokeWidth: settings.highlighterStrokeWidth,
      strokeColor: settings.highlighterStrokeColor,
    };
  }

  // Set stroke settings for a specific tool type
  setDrawToolSettings(toolType: DrawToolType, strokeWidth: number, strokeColor: string): void {
    if (toolType === DrawToolType.PEN) {
      this.updateUIProps({
        penStrokeWidth: strokeWidth,
        penStrokeColor: strokeColor,
      });
    } else {
      this.updateUIProps({
        highlighterStrokeWidth: strokeWidth,
        highlighterStrokeColor: strokeColor,
      });
    }
  }

  // Global theme preference methods
  get globalThemePreference(): string | undefined {
    return this.store.getValue().globalThemePreference;
  }

  setGlobalThemePreference(themeName: string | undefined): void {
    this.updateUIProps({ globalThemePreference: themeName });
  }
}
